import { Divider } from '@sixui/ui';
import { colorSchemeTokens } from '@sixui/ui/themes/base/colorScheme.stylex';
import { shapeTokens } from '@sixui/ui/themes/base/shape.stylex';
import stylex from '@stylexjs/stylex';

import type { IContainerProps } from '~/components/utils/Container';
import type { ITableContext } from './TableContext';
import { TableBody } from './TableBody';
import { TableBodyAction } from './TableBodyAction';
import { TableContext } from './TableContext';
import { TableHeader } from './TableHeader';
import { TableRow } from './TableRow';
import { TableSection } from './TableSection';
import { TableSortableHeader } from './TableSortableHeader';

export type ITableProps = IContainerProps &
  ITableContext & {
    children: React.ReactNode;

    /**
     * See:
     * - https://developer.mozilla.org/en-US/docs/Web/CSS/grid-template-columns
     * - https://css-tricks.com/almanac/properties/g/grid-template-columns/
     */
    gridTemplateColumns?: string;
  };

const styles = stylex.create({
  host: {
    display: 'flex',
    flexDirection: 'row',
    borderRadius: shapeTokens.corner$sm,
    color: colorSchemeTokens.onSurface,
  },
  inner: (gridTemplateColumns?: string) => ({
    flexDirection: 'column',
    overflowX: 'auto',
    display: 'grid',
    alignItems: 'center',
    gridTemplateColumns,
  }),
});

const Table: React.FC<ITableProps> = (props) => {
  const { sx, inFlow, children, gridTemplateColumns } = props;
  const contextValue: ITableContext = { inFlow };

  return (
    <TableContext.Provider value={contextValue}>
      {inFlow ? <Divider /> : null}
      <div
        {...stylex.props(styles.host, styles.inner(gridTemplateColumns), sx)}
      >
        {children}
      </div>
    </TableContext.Provider>
  );
};

const TableNamespace = Object.assign(Table, {
  Header: TableHeader,
  Section: TableSection,
  Row: TableRow,
  Body: TableBody,
  BodyAction: TableBodyAction,
  SortableHeader: TableSortableHeader,
});

export { TableNamespace as Table };
